import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Msg, panelGet, panelPost } from '../kit';

/**
 * Vocabulary, coercion, formats and the small dependency-free charts shared by
 * the /admin → Recrawl tabs (Backend/recrawl/engine.py, section "recrawl").
 *
 * Every payload is read defensively: the engine documents its shapes in the
 * engine.py docstring, but a run row written by an older worker can lack a
 * count, a guard can be {} and a manifest can omit any optional field, so
 * nothing here assumes a key exists — a missing value renders as "—".
 */

export const S = 'recrawl';

// ---------------------------------------------------------------------------
// Coercion
// ---------------------------------------------------------------------------
/** A finite number, or null (never NaN, never a boolean). */
export const num = (v: any): number | null => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
export const n0 = (v: any): number => num(v) ?? 0;
export const str = (v: any): string => (v === null || v === undefined ? '' : String(v));
export const arr = (v: any): any[] => (Array.isArray(v) ? v : []);
export const obj = (v: any): Record<string, any> =>
  (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
export const labelize = (k: string) =>
  str(k).replace(/^_/, '').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

// ---------------------------------------------------------------------------
// Formats
// ---------------------------------------------------------------------------
export const fmtN = (v: any): string => {
  const n = num(v);
  return n === null ? '—' : n.toLocaleString();
};
/** part / whole as "12%" (one decimal under 10%), '—' without a whole. */
export const fmtPct = (part: any, whole: any): string => {
  const p = num(part); const w = num(whole);
  if (p === null || !w) return '—';
  const x = (p / w) * 100;
  return `${x > 0 && x < 10 ? x.toFixed(1) : Math.round(x)}%`;
};
export const fmtUsd = (v: any): string => {
  const n = num(v);
  if (n === null) return '—';
  return `$${n > 0 && n < 0.1 ? n.toFixed(4) : n.toFixed(2)}`;
};
/** Seconds as "45 s", "12 m 3 s", "3 h 12 m", "2 d 4 h". */
export const fmtDur = (seconds: any): string => {
  const s = num(seconds);
  if (s === null) return '—';
  if (s < 1) return s > 0 ? '<1 s' : '0 s';
  if (s < 60) return `${Math.round(s)} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} m${s % 60 >= 1 ? ` ${Math.floor(s % 60)} s` : ''}`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h${m % 60 ? ` ${m % 60} m` : ''}`;
  return `${Math.floor(h / 24)} d${h % 24 ? ` ${h % 24} h` : ''}`;
};
export const fmtMsDur = (ms: any): string => {
  const n = num(ms);
  if (n === null) return '—';
  return n < 1000 ? `${Math.round(n)} ms` : fmtDur(n / 1000);
};
export const fmtBytes = (v: any): string => {
  const n = num(v);
  if (n === null) return '—';
  if (n < 1024) return `${n} B`;
  const u = ['KB', 'MB', 'GB', 'TB'];
  let x = n / 1024; let i = 0;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x < 10 ? x.toFixed(1) : Math.round(x)} ${u[i]}`;
};
const toDate = (v: any): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
/** The owner's calendar: every schedule and bucket is India time. */
export const fmtIST = (v: any, withTime = true): string => {
  const d = toDate(v);
  if (!d) return v ? String(v) : '—';
  const s = d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  });
  return withTime ? `${s} IST` : s;
};
/** "3 h ago" / "in 2 d" / "just now". */
export const fmtAgo = (v: any): string => {
  const d = toDate(v);
  if (!d) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  const a = Math.abs(diff);
  const t = a < 45 ? '' : a < 3600 ? `${Math.round(a / 60)} m` : a < 172800 ? `${Math.round(a / 3600)} h`
    : `${Math.round(a / 86400)} d`;
  if (!t) return 'just now';
  return diff >= 0 ? `${t} ago` : `in ${t}`;
};
/** A bucket key ('YYYY-MM-DD', the bucket's first day) as a short label. */
export const bucketLabel = (b: string, bucket: string, long = false): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(b));
  if (!m) return str(b);
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  if (bucket === 'month') {
    return d.toLocaleDateString('en-GB', { month: 'short', year: long ? 'numeric' : '2-digit', timeZone: 'UTC' });
  }
  const s = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(long ? { year: 'numeric' } : {}), timeZone: 'UTC' });
  return bucket === 'week' && long ? `week of ${s}` : s;
};
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** {enabled, every_days, weekday, hour_ist} as "every 30 d · Mon · 03:00 IST" (or "off"). */
export const scheduleText = (sc: any, withOff = true): string => {
  const s = obj(sc);
  const bits = [`every ${num(s.every_days) ?? '?'} d`];
  const wd = num(s.weekday);
  if (wd !== null && WEEKDAYS[wd]) bits.push(WEEKDAYS[wd]);
  const h = num(s.hour_ist);
  if (h !== null) bits.push(`${String(h).padStart(2, '0')}:00 IST`);
  return s.enabled ? bits.join(' · ') : withOff ? `off (${bits.join(' · ')})` : bits.join(' · ');
};

// ---------------------------------------------------------------------------
// Run / step / event vocabulary
// ---------------------------------------------------------------------------
export type Tone = 'blue' | 'amber' | 'red' | 'green' | 'grey' | 'purple' | 'navy';
const TONE_CLS: Record<Tone, string> = {
  blue: 'bg-blue-50 text-blue-800 border-blue-200',
  navy: 'bg-secondary-100 text-secondary-500 border-secondary-400',
  amber: 'bg-amber-50 text-amber-800 border-amber-300',
  red: 'bg-red-50 text-red-700 border-red-200',
  green: 'bg-green-50 text-green-800 border-green-200',
  grey: 'bg-grayscale-200 text-grayscale-1100 border-grayscale-600',
  purple: 'bg-purple-50 text-purple-800 border-purple-200',
};
export const TONE_DOT: Record<Tone, string> = {
  blue: '#2C6FC4', navy: '#2C5695', amber: '#D08A1E', red: '#D0443A', green: '#2F9E5B', grey: '#9A9A9A',
  purple: '#8B6BB8',
};

/** recrawl.runs.status → label + tone (DESIGN §5). */
export const RUN_STATUS: Record<string, { label: string; tone: Tone; hint: string }> = {
  queued: { label: 'queued', tone: 'navy', hint: 'Waiting for the one worker slot' },
  running: { label: 'running', tone: 'blue', hint: 'A worker is crawling / processing' },
  promoting: { label: 'promoting', tone: 'blue', hint: 'Writing the live corpus (after a backup)' },
  post_load: { label: 'post-load', tone: 'blue', hint: 'Promoted; the search layers are catching up' },
  awaiting_approval: { label: 'awaiting approval', tone: 'amber', hint: 'Staged — an admin must approve or reject' },
  held: { label: 'held', tone: 'amber', hint: 'Stopped for a person: partial scrape, blocked site or budget' },
  done: { label: 'done', tone: 'green', hint: 'Finished' },
  failed: { label: 'failed', tone: 'red', hint: 'A step failed; nothing was written live' },
  cancelled: { label: 'cancelled', tone: 'grey', hint: 'Cancelled or rejected by an admin' },
  interrupted: { label: 'interrupted', tone: 'red', hint: 'The worker vanished (restart / kill); resumable when it was a full run' },
};
export const runTone = (s: any): Tone => RUN_STATUS[str(s)]?.tone || 'grey';
export const LIVE_STATUSES = ['queued', 'running', 'promoting', 'post_load'];
export const isLive = (r: any) => LIVE_STATUSES.includes(str(obj(r).status));

/** recrawl.run_steps.status → colour (bars, dots). */
export const STEP_STATUS: Record<string, { tone: Tone; label: string }> = {
  ok: { tone: 'green', label: 'ok' },
  warning: { tone: 'amber', label: 'warning' },
  failed: { tone: 'red', label: 'failed' },
  skipped: { tone: 'grey', label: 'skipped' },
  running: { tone: 'blue', label: 'running' },
  pending: { tone: 'grey', label: 'pending' },
};
export const stepTone = (s: any): Tone => STEP_STATUS[str(s)]?.tone || 'grey';

export const MODE_LABEL: Record<string, string> = {
  full: 'full', dry_run: 'dry run', probe_only: 'probe', promote_only: 'promote (approved)',
};

/** master_events.event_type series, in the order the charts stack them. */
export const EVENT_SERIES = [
  { key: 'added', label: 'Added', color: '#2F9E5B' },
  { key: 'modified', label: 'Modified', color: '#2C6FC4' },
  { key: 'removed', label: 'Removed', color: '#D0443A' },
  { key: 'readded', label: 'Re-added', color: '#8B6BB8' },
  { key: 'moved', label: 'Moved (new URL)', color: '#1F7A8C' },
  { key: 'missing', label: 'Missing', color: '#D08A1E' },
  { key: 'manual_fix', label: 'Manual fix', color: '#9A9A9A' },
];
export const eventColor = (k: string) => EVENT_SERIES.find(e => e.key === k)?.color || '#9A9A9A';

/** staged_records.change_kind → tone. */
export const CHANGE_TONE: Record<string, Tone> = {
  new: 'green', modified: 'blue', readded: 'purple', moved: 'navy', removed: 'red', missing: 'amber', unchanged: 'grey',
};

// ---------------------------------------------------------------------------
// Moved technologies (same technology, new URL — master._match_moved)
// ---------------------------------------------------------------------------
/** master._match_moved rejection reasons → words. */
export const MOVED_REJECT_LABEL: Record<string, string> = {
  ref_not_unique_scrape: 'reference shared by several scraped records',
  ref_not_unique_master: 'reference shared by several master rows',
  ref_set_mismatch: 'different set of reference numbers',
  low_similarity: 'title and text disagree',
  new_url_live_other_id: 'new URL is live as another technology',
  id_live_elsewhere: 'the UTECH id is live at a third URL',
  old_url_live_other_id: 'old URL is live as another technology',
  live_other_university: 'live row belongs to another university',
};
/** manifest.moved_config source → words. */
export const MOVED_SOURCE_LABEL: Record<string, string> = {
  override: 'admin override', manifest: 'manifest', risk: 'risk flag / manual file', global: 'global setting',
};
/** _moved.live_state → words. */
export const MOVED_LIVE_LABEL: Record<string, string> = {
  at_old: 'at the old URL', at_new: 'already at the new URL', absent: 'at neither URL', unknown: 'not checked',
};
/** stats.moved_detection ("on" | "off:<source>") as words. */
export const movedDetectionText = (v: any): string => {
  const s = str(v);
  if (!s) return '';
  if (s === 'on') return 'on';
  const src = s.startsWith('off:') ? s.slice(4) : '';
  return src ? `off (${MOVED_SOURCE_LABEL[src] || src.replace(/_/g, ' ')})` : s.replace(/_/g, ' ');
};
const simText = (v: any) => (num(v) === null ? '—' : n0(v).toFixed(2));

/** A URL as a small truncated link. */
const UrlLink: React.FC<{ url: any; className?: string }> = ({ url, className }) => {
  const u = str(url);
  if (!u) return <span className="text-grayscale-1100">—</span>;
  return (
    <a href={u} target="_blank" rel="noreferrer noopener" onClick={e => e.stopPropagation()} title={u}
      className={`text-primary-700 hover:underline [overflow-wrap:anywhere] ${className || ''}`}>{u}</a>
  );
};

/** Candidate pairs the matcher did not apply, from the diff step's counts.stats: why each was
 *  rejected (moved_rejected + moved_rejected_samples) and, with detection off by a setting or an
 *  override, how many it would have paired (moved_would_match + moved_samples). */
export const movedNotApplied = (stats: any): number => {
  const st = obj(stats);
  const rejected = Object.values(obj(st.moved_rejected)).reduce((a: number, b: any) => a + n0(b), 0);
  return rejected + n0(st.moved_would_match);
};

export const MovedCandidates: React.FC<{ stats: any }> = ({ stats }) => {
  const st = obj(stats);
  const rej = Object.entries(obj(st.moved_rejected)).filter(([, v]) => n0(v) > 0).sort((a, b) => n0(b[1]) - n0(a[1]));
  const nRej = rej.reduce((a, [, v]) => a + n0(v), 0);
  const samples = arr(st.moved_rejected_samples).map(obj);
  const wouldMatch = num(st.moved_would_match);
  const would = wouldMatch !== null ? arr(st.moved_samples).map(obj) : [];
  const det = movedDetectionText(st.moved_detection);
  const facts = ([
    ['moved_candidates_master', 'vanished master rows'], ['moved_candidates_scraped', 'new scraped records'],
    ['scraped_no_ref', 'scraped without a reference'], ['master_no_ref', 'master rows without a reference'],
    ['master_no_id', 'master rows without a UTECH id'],
  ] as const).filter(([k]) => num(st[k]) !== null);
  const table = (rows: Record<string, any>[], withReason: boolean) => (
    <div className="overflow-auto rounded-lg border border-grayscale-300 max-h-80">
      <table className="w-full text-[12px]">
        <thead className="sticky top-0 bg-grayscale-200 text-[11px] text-grayscale-1200">
          <tr>{['Reference', 'Old title → new title', 'Title', 'Text', ...(withReason ? ['Reason'] : [])].map(h =>
            <th key={h} className="text-left px-2 py-1 whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-grayscale-200 align-top">
              <td className="px-2 py-1 font-mono text-[11px] whitespace-nowrap">{str(r.ref) || '—'}</td>
              <td className="px-2 py-1 min-w-[16rem]">
                <div className="text-grayscale-1300">{str(r.old_title) || '(no title)'}</div>
                <div className="text-grayscale-1300">→ {str(r.new_title) || '(no title)'}</div>
                <div className="text-[10.5px] flex flex-col"><UrlLink url={r.old_url} /><span>→ <UrlLink url={r.new_url} /></span></div>
              </td>
              <td className="px-2 py-1 whitespace-nowrap">{simText(r.title_sim)}</td>
              <td className="px-2 py-1 whitespace-nowrap">{simText(r.text_sim)}</td>
              {withReason && <td className="px-2 py-1 text-[11px]">{MOVED_REJECT_LABEL[str(r.reason)] || str(r.reason).replace(/_/g, ' ') || '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs text-grayscale-1100">
        {det && <>Moved detection: <b className="text-grayscale-1300">{det}</b>. </>}
        A vanished URL is paired with a new URL of the same university only when the reference number is unique on both sides and the
        title or text agrees; anything else stays one removal (missing) plus one addition.
      </div>
      {wouldMatch !== null && (
        <div className="text-[13px] text-grayscale-1300">
          <b>{fmtN(wouldMatch)}</b> possible move{wouldMatch === 1 ? '' : 's'} (detection off for this university): staged as new + missing.
        </div>
      )}
      {(n0(st.moved_healed) > 0 || Object.keys(obj(st.moved_heal_rejected)).length > 0) && (
        <div className="text-[12px] text-grayscale-1300">
          {n0(st.moved_healed) > 0 && <><b>{fmtN(st.moved_healed)}</b> unfinished earlier move{n0(st.moved_healed) === 1 ? '' : 's'} healed by the live id (staged as moved, whatever the switches say). </>}
          {Object.entries(obj(st.moved_heal_rejected)).filter(([, v]) => n0(v) > 0).map(([k, v]) => (
            <span key={k} className="text-amber-800">{fmtN(v)} could not be healed ({MOVED_REJECT_LABEL[k] || k.replace(/_/g, ' ')}): promote refuses the new URL until it is repaired. </span>
          ))}
        </div>
      )}
      {rej.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {rej.map(([k, v]) => (
            <Pill key={k} tone={k === 'low_similarity' ? 'amber' : 'grey'} title={k}>{MOVED_REJECT_LABEL[k] || k.replace(/_/g, ' ')} <b>{fmtN(v)}</b></Pill>
          ))}
        </div>
      )}
      {facts.length > 0 && (
        <div className="text-[11px] text-grayscale-1100">
          {facts.map(([k, label]) => `${fmtN(st[k])} ${label}`).join(' · ')}
        </div>
      )}
      {samples.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Rejected candidates ({samples.length < nRej ? `${samples.length} of ${fmtN(nRej)}` : samples.length})</div>
          {table(samples, true)}
        </div>
      )}
      {would.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Pairs it would make ({would.length < n0(wouldMatch) ? `${would.length} of ${fmtN(wouldMatch)}` : would.length})</div>
          {table(would, false)}
        </div>
      )}
      {!rej.length && wouldMatch === null && !samples.length && (
        <div className="text-xs text-grayscale-1100">No candidate was rejected.</div>
      )}
    </div>
  );
};

/** insights.recommend_throttle level → colour + words. */
export const HEALTH_LEVEL: Record<string, { tone: Tone; label: string }> = {
  ok: { tone: 'green', label: 'clean' },
  warn: { tone: 'amber', label: 'timeouts' },
  bad: { tone: 'red', label: 'blocked / throttled' },
  none: { tone: 'grey', label: 'no telemetry yet' },
};

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------
export const Pill: React.FC<{ tone?: Tone; children: React.ReactNode; title?: string; className?: string }> =
  ({ tone = 'grey', children, title, className }) => (
  <span title={title}
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-px text-[11px] font-medium whitespace-nowrap ${TONE_CLS[tone]} ${className || ''}`}>
    {children}
  </span>
);

export const Dot: React.FC<{ tone?: Tone; title?: string; size?: number }> = ({ tone = 'grey', title, size = 8 }) => (
  <span title={title} className="inline-block rounded-full shrink-0 align-middle"
    style={{ width: size, height: size, background: TONE_DOT[tone] }} />
);

export const StatusPill: React.FC<{ status: any; title?: string }> = ({ status, title }) => {
  const s = str(status);
  const spec = RUN_STATUS[s];
  return (
    <Pill tone={spec?.tone || 'grey'} title={title || spec?.hint}>
      {LIVE_STATUSES.includes(s) && s !== 'queued' && (
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      )}
      {spec?.label || s || '—'}
    </Pill>
  );
};

/** A label / value pair for the fact grids in the drawers. */
export const Fact: React.FC<{ label: string; children: React.ReactNode; title?: string; wide?: boolean }> =
  ({ label, children, title, wide }) => (
  <div className={`min-w-0 ${wide ? 'col-span-2' : ''}`} title={title}>
    <div className="text-[10.5px] uppercase tracking-wide text-grayscale-1100">{label}</div>
    <div className="text-[13px] text-grayscale-1300 [overflow-wrap:anywhere]">{children}</div>
  </div>
);

export const Facts: React.FC<{ children: React.ReactNode; cols?: string }> = ({ children, cols }) => (
  <div className={`grid gap-x-4 gap-y-2.5 ${cols || 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}>{children}</div>
);

/** A small headline tile (drawers use these where a KPI card is too big). */
export const Tile: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode; tone?: Tone; title?: string }> =
  ({ label, value, hint, tone, title }) => (
  <div title={title}
    className={`rounded-lg border px-3 py-2 min-w-[7.5rem] flex-1 ${tone ? TONE_CLS[tone] : 'border-grayscale-300 bg-white'}`}>
    <div className="text-[10.5px] uppercase tracking-wide opacity-80">{label}</div>
    <div className="text-lg font-bold font-grotesk leading-tight">{value}</div>
    {hint !== undefined && hint !== null && hint !== '' && <div className="text-[11px] opacity-80">{hint}</div>}
  </div>
);

/** Pretty JSON in a scroll box (the raw guard, counts, settings snapshot …). */
export const JsonBox: React.FC<{ value: any; maxH?: string }> = ({ value, maxH = '16rem' }) => {
  let text = '';
  try { text = JSON.stringify(value, null, 2); } catch { text = String(value); }
  return (
    <pre className="text-[11px] leading-snug font-mono bg-grayscale-200 rounded-lg p-2.5 overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere]"
      style={{ maxHeight: maxH }}>{text || '—'}</pre>
  );
};

/** Click-to-open block, closed by default. */
export const Collapse: React.FC<{ title: React.ReactNode; children: React.ReactNode; open?: boolean; right?: React.ReactNode }> =
  ({ title, children, open: initial, right }) => {
  const [open, setOpen] = useState(!!initial);
  return (
    <div className="rounded-lg border border-grayscale-300 bg-white">
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left text-sm font-medium text-grayscale-1300">
          <span className="text-grayscale-1100 text-xs w-3">{open ? '▾' : '▸'}</span>
          <span className="min-w-0 truncate">{title}</span>
        </button>
        {right}
      </div>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

/** An on/off switch (same look as the other admin toggles). */
export const Switch: React.FC<{ on: boolean; onChange: (v: boolean) => void; disabled?: boolean; title?: string; label?: string }> =
  ({ on, onChange, disabled, title, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label || title} disabled={disabled} title={title}
    onClick={e => { e.stopPropagation(); onChange(!on); }}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors
      ${on ? 'bg-primary-500' : 'bg-grayscale-600'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
  </button>
);

export const NumInput: React.FC<{
  value: string; onChange: (v: string) => void; min?: number; max?: number; step?: number; placeholder?: string;
  width?: string; invalid?: boolean; disabled?: boolean;
}> = ({ value, onChange, min, max, step, placeholder, width = 'w-24', invalid, disabled }) => (
  <input type="number" value={value} min={min} max={max} step={step} placeholder={placeholder} disabled={disabled}
    onChange={e => onChange(e.target.value)}
    className={`h-8 px-2 rounded-md border-[0.5px] bg-white text-sm ${width} ${invalid ? 'border-red-500 ring-1 ring-red-300' : 'border-primary-300'} disabled:opacity-50`} />
);

/** Chips that pick one value; each can carry a count. */
export const ChipPick: React.FC<{
  options: { key: string; label: string; count?: number | null; tone?: Tone }[];
  value: string; onChange: (k: string) => void;
}> = ({ options, value, onChange }) => (
  <div className="flex gap-1 flex-wrap">
    {options.map(o => (
      <button key={o.key} type="button" onClick={() => onChange(o.key)}
        className={`py-1 px-2.5 rounded-full text-xs font-grotesk whitespace-nowrap border ${value === o.key
          ? 'bg-primary-500 border-primary-500 text-grayscale-100 font-bold'
          : 'border-grayscale-600 text-grayscale-1200 hover:bg-grayscale-200'}`}>
        {o.tone && value !== o.key && <Dot tone={o.tone} size={7} />} {o.label}
        {o.count !== undefined && o.count !== null && <span className="opacity-75"> {o.count.toLocaleString()}</span>}
      </button>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Drawer stacking: Escape closes the drawer on top, and only that one
// ---------------------------------------------------------------------------
// Every recrawl drawer (university, run, the heatmap drill-downs) is the same fixed z-50 layer, so the
// one painted on top is the one latest in document order: a drawer opened from another is rendered
// either inside it (the heatmap of the university drawer) or after it (a run over its university).
// Each drawer registers its close function under an id and wraps itself in
// <div className="contents" data-recrawl-layer={id}>; RecrawlPanel's single Escape handler closes the
// last visible one. A drawer in a hidden tab (display: none) is skipped.
const layerClosers = new Map<string, () => void>();
let layerSeq = 0;

/** Registers a drawer as an Escape layer; returns the id for its data-recrawl-layer wrapper. */
export function useRecrawlLayer(close: () => void): string {
  const idRef = useRef('');
  if (!idRef.current) idRef.current = `layer${++layerSeq}`;
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    const id = idRef.current;
    layerClosers.set(id, () => closeRef.current());
    return () => { layerClosers.delete(id); };
  }, []);
  return idRef.current;
}

/** Closes the visible drawer on top; false when none is open. */
export function closeTopRecrawlLayer(): boolean {
  const els = document.querySelectorAll<HTMLElement>('[data-recrawl-layer]');
  for (let i = els.length - 1; i >= 0; i -= 1) {
    const el = els[i];
    const shown = el.firstElementChild ? el.firstElementChild.getClientRects().length > 0 : false;
    const close = layerClosers.get(el.dataset.recrawlLayer || '');
    if (shown && close) { close(); return true; }
  }
  return false;
}

/** The wrapper that marks a drawer as an Escape layer (display: contents, so it adds no box). */
export const Layer: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <div className="contents" data-recrawl-layer={id}>{children}</div>
);

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
/** Calls cb every `ms` while `active` (skipped while the tab is hidden). */
export function usePoll(cb: () => void, ms: number, active: boolean) {
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => {
    if (!active) return undefined;
    const t = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      ref.current();
    }, ms);
    return () => window.clearInterval(t);
  }, [ms, active]);
}

/**
 * useAdminGet for the views that watch a live run (Overview, Runs, the run
 * drawer). The difference is what a failed request does. useAdminGet clears
 * `data`, and these views work out "is a run live?" — and so whether they keep
 * polling — from that data: one dropped poll, or the API restarting during an
 * hours-long run (workers survive API restarts, DESIGN §1), blanked the view
 * and stopped its polling until it was closed and reopened. Here a failed
 * request keeps the last good answer of the same query beside the error
 * (`stale`), so the poller keeps going and the view recovers on the next
 * answer. An answer to a different query (another run id, other filters) is
 * never kept across an error.
 */
export function useLiveGet<T = any>(action: string, params: Record<string, string | number>, deps: any[]) {
  const key = JSON.stringify(params);
  const [state, setState] = useState<{ data: T | null; key: string; loading: boolean; error: string }>(
    { data: null, key, loading: true, error: '' });
  // Last request wins, as in useAdminGet.
  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    // The error stays until the answer, so a failing poll does not flash its message on and off.
    setState(s => ({ ...s, loading: true }));
    const { data, error } = await panelGet(S, action, params);
    if (mine !== seq.current) return;
    setState(s => (error
      ? { data: s.key === key ? s.data : null, key, loading: false, error }
      : { data: data as T, key, loading: false, error: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data: state.data, loading: state.loading, error: state.error,
           stale: !!state.error && state.data !== null, reload: load };
}

/** A failed load with a Retry button. With `stale`, the view still shows its last good answer under it. */
export const RetryMsg: React.FC<{ error: string; onRetry: () => void; stale?: boolean; polling?: boolean }> =
  ({ error, onRetry, stale, polling }) => (
  <Msg kind="error">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="flex-1 min-w-0">
        {stale ? `Could not refresh: ${error}. Showing the last state loaded${polling ? '; trying again automatically' : ''}.` : error}
      </span>
      <Btn small onClick={onRetry}>{stale ? 'Retry now' : 'Retry'}</Btn>
    </div>
  </Msg>
);

/** A value that settles `ms` after the last change (search boxes). */
export function useDebounced<T>(v: T, ms = 350): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = window.setTimeout(() => setD(v), ms);
    return () => window.clearTimeout(t);
  }, [v, ms]);
  return d;
}

export type Note = { kind: 'ok' | 'error'; text: string } | null;

/** POST helper with one busy key and one message line per component. */
export function useAction(onDone?: () => void) {
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState<Note>(null);
  const done = useRef(onDone);
  done.current = onDone;
  const post = useCallback(async (key: string, action: string, body: Record<string, any>,
                                 ok?: (d: any) => string): Promise<any | null> => {
    setBusy(key); setNote(null);
    const { data, error } = await panelPost(S, action, body);
    setBusy('');
    if (error) { setNote({ kind: 'error', text: error }); return null; }
    setNote({ kind: 'ok', text: ok ? ok(data) : 'Done.' });
    done.current?.();
    return data ?? {};
  }, []);
  return { busy, note, setNote, post };
}

export const NoteLine: React.FC<{ note: Note; onClose?: () => void }> = ({ note, onClose }) => (
  note ? (
    <div className="flex items-start gap-2">
      <div className="flex-1"><Msg kind={note.kind}>{note.text}</Msg></div>
      {onClose && <Btn small onClick={onClose}>×</Btn>}
    </div>
  ) : null
);

// ---------------------------------------------------------------------------
// Cross-tab context
// ---------------------------------------------------------------------------
export interface UniRow {
  code: string; name: string; folder?: string; kind?: string; platform?: string; country?: string;
  portal_url?: string; risk: string[]; enabled: boolean; paused: boolean; needs_attention: boolean;
  note?: string | null; overridden?: boolean; schedule: Record<string, any>;
  master: { active?: number; missing?: number; removed?: number }; live_count?: number | null; drift?: number | null;
  /** The newest run of any mode (a probe included). */
  last_run: any | null;
  /** The newest full / dry run / approved (promote) run: the results the grid and the drawer show. */
  last_scrape: any | null;
  /** The newest finished probe of any run: site reachability. */
  last_probe: ProbeView | null;
  last_success_at?: string | null; open_run_id?: number | null;
  consecutive_failures?: number; next_due_at?: string | null; due?: boolean; due_reason?: string | null;
  /** Status of the open run (any mode: a queued dry run or probe blocks a new run too). */
  open_run_status?: string | null;
  /** Manual-file kind only: a source file is on the server. */
  has_upload?: boolean | null;
  /** Why each run mode cannot start now (null = it can). */
  run_blocked?: Partial<Record<'probe_only' | 'dry_run' | 'full', string | null>>;
  /** Roughly what the next dry / full run would enrich, from the latest results. */
  /** records / usd are null when the latest results are already on the website (applied). */
  enrich_estimate?: { records?: number | null; usd?: number | null; applied?: boolean; from_run_id?: number; per_run_cap_usd?: number } | null;
}
export interface ProbeView {
  run_id?: number | null; mode?: string | null; run_status?: string | null; status?: string;
  level: string; at?: string | null; targets?: number | null; ok?: number | null; blocked?: number | null;
  samples_checked?: boolean; portal_from?: string | null; warnings: string[]; reasons: string[]; note?: string | null;
}
export interface HealthRow {
  code: string; name?: string; runs: any[]; latest: any | null;
  recommendation: { level?: string; text?: string; suggest?: Record<string, any> | null; notes?: string[] };
  declared: any[]; effective: Record<string, any>;
}

export interface RecrawlCtx {
  universities: UniRow[];
  uniByCode: Map<string, UniRow>;
  health: Map<string, HealthRow>;
  /** Bumped after every change made from the panel: tabs reload on it. */
  tick: number;
  reloadAll: () => void;
  openUniversity: (code: string) => void;
  openRun: (id: number | string) => void;
  goFlow: (code: string, runId?: number | string) => void;
  goRuns: (f: { code?: string; status?: string }) => void;
  goChanges: (f: { code?: string; tech?: string }) => void;
  goTab: (t: string) => void;
}

export const toUni = (u: any): UniRow => {
  const o = obj(u);
  return {
    ...o,
    code: str(o.code).toUpperCase(), name: str(o.name || o.code), risk: arr(o.risk).map(str),
    enabled: o.enabled !== false, paused: !!o.paused, needs_attention: !!o.needs_attention,
    schedule: obj(o.schedule), master: obj(o.master), last_run: o.last_run && typeof o.last_run === 'object' ? o.last_run : null,
    last_scrape: o.last_scrape && typeof o.last_scrape === 'object' ? o.last_scrape : null,
    last_probe: toProbe(o.last_probe),
  } as UniRow;
};
export const toProbe = (p: any): ProbeView | null => (p && typeof p === 'object' && !Array.isArray(p)
  ? { ...p, level: str(p.level) || 'ok', warnings: arr(p.warnings).map(str), reasons: arr(p.reasons).map(str) }
  : null);

// ---------------------------------------------------------------------------
// A university's latest scrape results and latest probe (grid, table, drawer)
// ---------------------------------------------------------------------------
const RESULT_KEYS: [string, string, string][] = [
  // [counts key, long label, short label]
  ['mapped', 'mapped', 'mapped'], ['cleaned', 'cleaned', 'clean'], ['new', 'new', 'new'],
  ['modified', 'modified', 'mod'], ['moved', 'moved', 'moved'], ['missing', 'missing', 'missing'],
];
/** A run's results as "121 mapped · 119 cleaned · 4 new · 1 modified · 0 moved · 2 missing" ('' when it has none). */
export const scrapeResults = (run: any, short = false): string => {
  const c = obj(obj(run).counts);
  return RESULT_KEYS.filter(([k]) => num(c[k]) !== null)
    .map(([k, long, sh]) => `${fmtN(c[k])} ${short ? sh : long}`).join(' · ');
};
/** "next in the queue" / "#3 in the queue" for a queued run ('' otherwise). Runs start one at a time. */
export const queueText = (run: any): string => {
  const r = obj(run);
  const p = num(r.queue_position);
  if (str(r.status) !== 'queued' || !p) return '';
  return p === 1 ? 'next in the queue' : `#${p} in the queue`;
};
/** "dry run · done · 3 h ago" for a scrape run ("dry run · queued · #3 in the queue" while it waits). */
export const scrapeLine = (run: any): string => {
  const r = obj(run);
  const st = str(r.status);
  if (!st) return '';
  const when = queueText(r) || fmtAgo(r.finished_at || r.started_at || r.created_at);
  return [MODE_LABEL[str(r.mode)] || str(r.mode), RUN_STATUS[st]?.label || st.replace(/_/g, ' '), when].filter(Boolean).join(' · ');
};

export const PROBE_LEVEL: Record<string, { tone: Tone; label: string }> = {
  ok: { tone: 'green', label: 'site reachable' },
  warning: { tone: 'amber', label: 'reachable, with warnings' },
  blocked: { tone: 'red', label: 'blocked or unreachable' },
  error: { tone: 'red', label: 'probe failed' },
};
/** The tooltip of a probe: result, when, which run, warnings. */
export const probeTitle = (p: ProbeView | null): string => {
  if (!p) return 'No probe yet';
  const spec = PROBE_LEVEL[p.level] || PROBE_LEVEL.ok;
  const lines = [`Latest probe: ${spec.label}`, `${fmtIST(p.at)}${num(p.run_id) !== null ? ` · run #${p.run_id} (${MODE_LABEL[str(p.mode)] || str(p.mode)})` : ''}`];
  if (num(p.targets) !== null) lines.push(`${fmtN(p.ok)} of ${fmtN(p.targets)} pages answered${n0(p.blocked) ? ` · ${fmtN(p.blocked)} blocked` : ''}`);
  if (p.samples_checked === false) lines.push('Sample technology pages are not checked for this university');
  if (p.reasons.length) lines.push(`Reasons: ${p.reasons.map(r => r.replace(/_/g, ' ')).join(', ')}`);
  if (p.warnings.length) lines.push(`Warnings: ${p.warnings.join('; ')}`);
  return lines.join('\n');
};
/** Small reachability indicator: a dot, "site reachable", and how long ago. */
export const ProbeBadge: React.FC<{ probe: ProbeView | null; ctx?: RecrawlCtx; className?: string }> = ({ probe, ctx, className }) => {
  if (!probe) return null;
  const spec = PROBE_LEVEL[probe.level] || PROBE_LEVEL.ok;
  return (
    <span title={probeTitle(probe)} className={`inline-flex items-center gap-1 min-w-0 ${className || ''}`}>
      <Dot tone={spec.tone} size={7} />
      <span className="truncate">{spec.label}{probe.at ? ` · ${fmtAgo(probe.at)}` : ''}</span>
      {ctx && num(probe.run_id) !== null && <RunLink ctx={ctx} id={probe.run_id} />}
    </span>
  );
};
export const toHealth = (h: any): HealthRow => {
  const o = obj(h);
  return { ...o, code: str(o.code).toUpperCase(), runs: arr(o.runs), latest: o.latest || null,
           recommendation: obj(o.recommendation), declared: arr(o.declared), effective: obj(o.effective) } as HealthRow;
};
export const uniLabel = (ctx: RecrawlCtx, code: any) => {
  const c = str(code).toUpperCase();
  const u = ctx.uniByCode.get(c);
  return u ? `${c} — ${u.name}` : c || '—';
};

/** "CODE" as a link that opens the university drawer. */
export const UniLink: React.FC<{ ctx: RecrawlCtx; code: any; name?: boolean }> = ({ ctx, code, name }) => {
  const c = str(code).toUpperCase();
  if (!c) return <span>—</span>;
  const u = ctx.uniByCode.get(c);
  return (
    <button type="button" onClick={e => { e.stopPropagation(); ctx.openUniversity(c); }}
      title={u ? `${u.name} — open` : 'Open'}
      className="text-primary-700 hover:underline underline-offset-2 font-medium whitespace-nowrap">
      {c}{name && u ? <span className="text-grayscale-1100 font-normal"> · {u.name}</span> : null}
    </button>
  );
};

export const RunLink: React.FC<{ ctx: RecrawlCtx; id: any }> = ({ ctx, id }) => (
  num(id) === null ? <span>—</span> : (
    <button type="button" onClick={e => { e.stopPropagation(); ctx.openRun(String(id)); }}
      className="text-primary-700 hover:underline underline-offset-2 font-medium whitespace-nowrap">#{id}</button>
  )
);

// ---------------------------------------------------------------------------
// Charts (dependency-free, the MonthTrend / DailyBars pattern)
// ---------------------------------------------------------------------------
export type Series = { key: string; label: string; color: string };

/** Stacked columns, one per row: hover a column for its numbers, click to pick
 *  it (when onPick is given), legend chips switch series on and off. */
export const StackedColumns: React.FC<{
  rows: Record<string, any>[]; xKey: string; series: Series[]; height?: number;
  xLabel?: (x: string, long?: boolean) => string; onPick?: (row: Record<string, any>) => void; picked?: string;
  empty?: string; initiallyOff?: string[]; unit?: string;
}> = ({ rows, xKey, series, height = 176, xLabel, onPick, picked, empty, initiallyOff, unit }) => {
  const [off, setOff] = useState<string[]>(initiallyOff || []);
  const [hover, setHover] = useState<number | null>(null);
  const on = series.filter(s => !off.includes(s.key));
  const totals = rows.map(r => on.reduce((a, s) => a + Math.max(0, n0(r[s.key])), 0));
  const max = Math.max(1, ...totals);
  const lab = (x: string, long = false) => (xLabel ? xLabel(x, long) : x);
  const sumAll = totals.reduce((a, b) => a + b, 0);
  const shown = hover !== null ? rows[hover] : null;
  const few = rows.length <= 8;       // a handful of columns: keep them column-width, not full-width slabs
  return (
    <div className="flex flex-col gap-2 min-w-0">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {series.map(s => {
            const isOn = !off.includes(s.key);
            const tot = rows.reduce((a, r) => a + Math.max(0, n0(r[s.key])), 0);
            return (
              <button key={s.key} type="button"
                onClick={() => setOff(o => (isOn ? [...o, s.key] : o.filter(k => k !== s.key)))}
                title={isOn ? `Hide ${s.label}` : `Show ${s.label}`}
                className={`inline-flex items-center gap-1.5 py-0.5 px-2 rounded-full text-[11px] border ${isOn
                  ? 'border-grayscale-600 text-grayscale-1300 bg-white' : 'border-grayscale-300 text-grayscale-1100 opacity-50 line-through'}`}>
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                {s.label} <b>{tot.toLocaleString()}</b>
              </button>
            );
          })}
        </div>
        <div className="text-xs text-grayscale-1100 min-h-[1rem]" aria-live="polite">
          {shown ? (
            <>
              <b className="text-grayscale-1300">{lab(str(shown[xKey]), true)}</b>
              {on.map(s => <span key={s.key}> · {s.label.toLowerCase()} <b className="text-grayscale-1300">{n0(shown[s.key]).toLocaleString()}</b></span>)}
            </>
          ) : <>Total <b className="text-grayscale-1300">{sumAll.toLocaleString()}</b>{unit ? ` ${unit}` : ''} over {rows.length} column{rows.length === 1 ? '' : 's'}</>}
        </div>
      </div>
      {!rows.length ? (
        <div className="text-sm text-grayscale-1100 py-6 text-center">{empty || 'Nothing in this period.'}</div>
      ) : (
        <div className="flex gap-2">
          <div className="flex flex-col justify-between text-[10px] text-grayscale-1100 text-right w-10 shrink-0" style={{ height }}>
            <span>{max.toLocaleString()}</span><span>{Math.round(max / 2).toLocaleString()}</span><span>0</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="relative border-b border-grayscale-600/40" style={{ height }} onMouseLeave={() => setHover(null)}>
              <div aria-hidden className="absolute inset-x-0 top-0 border-t border-dashed border-grayscale-600/30" />
              <div aria-hidden className="absolute inset-x-0 top-1/2 border-t border-dashed border-grayscale-600/30" />
              <div className={`absolute inset-0 flex items-end ${rows.length > 60 ? 'gap-px' : 'gap-[3px]'} ${few ? 'justify-around' : ''}`}>
                {rows.map((r, i) => {
                  const x = str(r[xKey]);
                  const isPicked = picked !== undefined && picked === x;
                  return (
                    <div key={x || i} onMouseEnter={() => setHover(i)}
                      onClick={onPick ? () => onPick(r) : undefined}
                      title={`${lab(x, true)}: ${on.map(s => `${s.label} ${n0(r[s.key]).toLocaleString()}`).join(' · ')}`}
                      className={`flex-1 ${few ? 'max-w-[4.5rem]' : ''} h-full flex flex-col justify-end rounded-t ${onPick ? 'cursor-pointer' : ''} ${isPicked ? 'bg-primary-100' : hover === i ? 'bg-grayscale-200' : ''}`}>
                      {on.map(s => {
                        const v = Math.max(0, n0(r[s.key]));
                        if (!v) return null;
                        return <div key={s.key} style={{ height: `max(1px, ${(v / max) * 100}%)`, background: s.color }} />;
                      }).reverse()}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={`flex text-[10px] text-grayscale-1100 mt-1 ${few ? 'justify-around gap-[3px]' : ''}`}>
              {rows.length <= 16 ? rows.map((r, i) => (
                <span key={i} className={`flex-1 ${few ? 'max-w-[4.5rem]' : ''} text-center truncate ${picked === str(r[xKey]) ? 'font-bold text-grayscale-1300' : ''}`}>
                  {lab(str(r[xKey]))}
                </span>
              )) : (
                <div className="flex justify-between w-full">
                  <span>{lab(str(rows[0][xKey]))}</span>
                  <span>{lab(str(rows[Math.floor(rows.length / 2)][xKey]))}</span>
                  <span>{lab(str(rows[rows.length - 1][xKey]))}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** A line over buckets (coverage: listed technologies at the end of each bucket). */
export const LineChart: React.FC<{
  points: { x: string; y: number }[]; height?: number; xLabel?: (x: string) => string; color?: string;
  yFormat?: (v: number) => string;
}> = ({ points, height = 120, xLabel, color = '#2C5695', yFormat }) => {
  const [hover, setHover] = useState<number | null>(null);
  if (!points.length) return <div className="text-sm text-grayscale-1100 py-4 text-center">No data.</div>;
  const ys = points.map(p => p.y);
  const lo0 = Math.min(...ys); const hi0 = Math.max(...ys);
  const pad = Math.max(1, (hi0 - lo0) * 0.1);
  const lo = Math.max(0, lo0 - pad); const hi = hi0 + pad;
  const W = 600; const H = 100;
  const xAt = (i: number) => (points.length === 1 ? W / 2 : (i / (points.length - 1)) * W);
  const yAt = (v: number) => H - ((v - lo) / Math.max(1, hi - lo)) * H;
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(p.y).toFixed(1)}`).join(' ');
  const fy = yFormat || ((v: number) => Math.round(v).toLocaleString());
  const h = hover !== null ? points[hover] : null;
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs text-grayscale-1100 min-h-[1rem]">
        {h ? <><b className="text-grayscale-1300">{fy(h.y)}</b> at {xLabel ? xLabel(h.x) : h.x}</>
          : <>from <b className="text-grayscale-1300">{fy(points[0].y)}</b> to <b className="text-grayscale-1300">{fy(points[points.length - 1].y)}</b>
            {' '}({points[points.length - 1].y - points[0].y >= 0 ? '+' : ''}{fy(points[points.length - 1].y - points[0].y)})</>}
      </div>
      <div className="flex gap-2">
        <div className="flex flex-col justify-between text-[10px] text-grayscale-1100 text-right w-12 shrink-0" style={{ height }}>
          <span>{fy(hi)}</span><span>{fy(lo)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}
            onMouseLeave={() => setHover(null)}
            onMouseMove={e => {
              const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
              const f = (e.clientX - r.left) / Math.max(1, r.width);
              setHover(Math.max(0, Math.min(points.length - 1, Math.round(f * (points.length - 1)))));
            }}>
            <path d={`${d} L${xAt(points.length - 1)},${H} L${xAt(0)},${H} Z`} fill={color} opacity={0.08} />
            <path d={d} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
            {hover !== null && (
              <line x1={xAt(hover)} x2={xAt(hover)} y1={0} y2={H} stroke="#AF8E5E" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            )}
          </svg>
          <div className="flex justify-between text-[10px] text-grayscale-1100 mt-1">
            <span>{xLabel ? xLabel(points[0].x) : points[0].x}</span>
            <span>{xLabel ? xLabel(points[points.length - 1].x) : points[points.length - 1].x}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/** A thin horizontal bar split into coloured parts (ranked lists, request mixes). */
export const SplitBar: React.FC<{ parts: { key: string; value: number; color: string; label?: string }[]; total?: number; title?: string; h?: string }> =
  ({ parts, total, title, h = 'h-2.5' }) => {
  const sum = total ?? parts.reduce((a, p) => a + Math.max(0, p.value), 0);
  return (
    <div title={title} className={`flex ${h} w-full rounded-full bg-grayscale-200 overflow-hidden`}>
      {sum > 0 && parts.map(p => p.value > 0 && (
        <div key={p.key} title={`${p.label || p.key}: ${p.value.toLocaleString()}`} className="h-full"
          style={{ width: `${(p.value / sum) * 100}%`, background: p.color }} />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Field diffs (hashing.compact_change shapes)
// ---------------------------------------------------------------------------
const show = (v: any): string => {
  if (v === null || v === undefined || v === '') return '∅';
  if (Array.isArray(v)) return v.length ? v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(' · ') : '[]';
  if (typeof v === 'object') { try { return JSON.stringify(v); } catch { return String(v); } }
  return String(v);
};

/** One field's change: {before, after} | {before_len, after_len, sample:{at, before, after}}, plus
 *  added / removed list items when the field is a list. */
export const ChangeView: React.FC<{ field: string; change: any }> = ({ field, change }) => {
  const c = obj(change);
  const hasBA = 'before' in c || 'after' in c;
  const sample = obj(c.sample);
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-grayscale-300 bg-white p-2">
      <div className="flex items-center gap-2 flex-wrap">
        <code className="text-[11.5px] font-mono font-bold text-grayscale-1300">{field}</code>
        {num(c.before_len) !== null && (
          <span className="text-[11px] text-grayscale-1100">
            {fmtN(c.before_len)} → {fmtN(c.after_len)} chars ({n0(c.after_len) - n0(c.before_len) >= 0 ? '+' : ''}{fmtN(n0(c.after_len) - n0(c.before_len))})
          </span>
        )}
      </div>
      {hasBA && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[12px]">
          <div className="rounded bg-red-50 border border-red-100 px-2 py-1 text-red-900 [overflow-wrap:anywhere] whitespace-pre-wrap">
            <span className="text-[10px] uppercase tracking-wide text-red-700 block">before</span>{show(c.before)}
          </div>
          <div className="rounded bg-green-50 border border-green-100 px-2 py-1 text-green-900 [overflow-wrap:anywhere] whitespace-pre-wrap">
            <span className="text-[10px] uppercase tracking-wide text-green-700 block">after</span>{show(c.after)}
          </div>
        </div>
      )}
      {!hasBA && (sample.before !== undefined || sample.after !== undefined) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[12px]">
          <div className="rounded bg-red-50 border border-red-100 px-2 py-1 text-red-900 [overflow-wrap:anywhere] whitespace-pre-wrap">
            <span className="text-[10px] uppercase tracking-wide text-red-700 block">before (around char {fmtN(sample.at)})</span>…{show(sample.before)}…
          </div>
          <div className="rounded bg-green-50 border border-green-100 px-2 py-1 text-green-900 [overflow-wrap:anywhere] whitespace-pre-wrap">
            <span className="text-[10px] uppercase tracking-wide text-green-700 block">after</span>…{show(sample.after)}…
          </div>
        </div>
      )}
      {(arr(c.added).length > 0 || arr(c.removed).length > 0) && (
        <div className="flex flex-wrap gap-1 text-[11px]">
          {arr(c.added).map((x, i) => <span key={`a${i}`} className="rounded-full bg-green-100 text-green-800 px-1.5">+ {show(x)}</span>)}
          {arr(c.removed).map((x, i) => <span key={`r${i}`} className="rounded-full bg-red-100 text-red-700 px-1.5 line-through">− {show(x)}</span>)}
          {(num(c.n_added) !== null || num(c.n_removed) !== null) && (
            <span className="text-grayscale-1100">({fmtN(c.n_added)} added, {fmtN(c.n_removed)} removed in all)</span>
          )}
        </div>
      )}
    </div>
  );
};

/** A diff object {field: change}; changed_fields without a stored diff are listed as names. Keys
 *  starting with "_" are bookkeeping (_moved, _changed, _needs_payload, _was_status), never fields;
 *  `omit` hides fields shown elsewhere (a moved row's URL pair). */
export const DiffView: React.FC<{ diff: any; fields?: any; omit?: string[] }> = ({ diff, fields, omit }) => {
  const d = obj(diff);
  const keys = Object.keys(d).filter(k => !k.startsWith('_') && !(omit || []).includes(k));
  const extra = arr(fields).map(str).filter(f => f && !keys.includes(f) && !f.startsWith('_') && !(omit || []).includes(f));
  if (!keys.length && !extra.length) return <div className="text-xs text-grayscale-1100">No field-level diff stored.</div>;
  return (
    <div className="flex flex-col gap-1.5">
      {keys.map(k => <ChangeView key={k} field={k} change={d[k]} />)}
      {extra.length > 0 && (
        <div className="text-[11px] text-grayscale-1100">Also changed (no before/after stored): {extra.join(', ')}</div>
      )}
    </div>
  );
};

export const FieldChips: React.FC<{ fields: any; max?: number; quiet?: boolean }> = ({ fields, max = 6, quiet }) => {
  const f = arr(fields).map(str).filter(Boolean);
  if (!f.length) return quiet ? null : <span className="text-grayscale-1100">—</span>;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {f.slice(0, max).map(x => <span key={x} className="rounded bg-blue-50 text-blue-800 px-1.5 text-[10.5px] font-mono">{x}</span>)}
      {f.length > max && <span className="text-[10.5px] text-grayscale-1100">+{f.length - max}</span>}
    </span>
  );
};

/** The run status mix of a list (ranked, with % and counts). */
export const useStatusMix = (runs: any[]) => useMemo(() => {
  const m = new Map<string, number>();
  runs.forEach(r => { const s = str(obj(r).status) || '?'; m.set(s, (m.get(s) || 0) + 1); });
  return Array.from(m.entries()).sort((a, b) => b[1] - a[1]).map(([status, n]) => ({ status, n }));
}, [runs]);
